import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AUTH_API, apiUrl } from '@/config/api';
import { useAuth } from '@/contexts/AuthContext';
import UserAvatar from '@/components/brand/UserAvatar';
import { useMemberApp } from '../AppProvider';
import { getAuthToken } from '@/utils/auth';
import { IconArrow, IconClose, IconCrown, IconDoc, IconMic } from '../components/Icons';
import { memberPath } from '@/config/hosts';
import { TEXT_LANGUAGE_CHIPS } from '@/voice/languages';

function authHeaders() {
  const headers = {};
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function downloadBlob(blob, filename) {
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(href);
}

const WRITING_LANGS = [
  ...TEXT_LANGUAGE_CHIPS.filter((row) => row.code === 'en'),
  ...TEXT_LANGUAGE_CHIPS.filter((row) => row.code !== 'en'),
];

export default function SettingsPage() {
  const { user, updateUser } = useAuth();
  const { isPro, setUpgradeOpen } = useMemberApp();
  const lockedName = Boolean(String(user?.full_name || user?.name || '').trim());
  const lockedPhone = Boolean(String(user?.phone || '').trim());
  const [name, setName] = useState(user?.full_name || user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [language, setLanguage] = useState(user?.preferred_language || 'en');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setName(user?.full_name || user?.name || '');
    setPhone(user?.phone || '');
    setLanguage(user?.preferred_language || 'en');
  }, [user]);

  const languageLabel = useMemo(
    () => WRITING_LANGS.find((row) => row.code === language)?.label || 'English',
    [language],
  );

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      preferred_language: language || 'en',
    };
    if (!lockedName) {
      const next = name.trim();
      if (!next) {
        setNote('Please add your full name the first time.');
        return;
      }
      payload.full_name = next;
    }
    if (!lockedPhone) {
      const nextPhone = phone.trim();
      if (nextPhone) payload.phone = nextPhone;
    }

    setBusy(true);
    setNote('');
    try {
      const { data } = await axios.put(`${AUTH_API}/profile`, payload, {
        withCredentials: true,
        headers: authHeaders(),
      });
      if (updateUser) {
        updateUser({
          ...user,
          ...(data || {}),
          full_name: data?.full_name ?? payload.full_name ?? user?.full_name,
          phone: data?.phone ?? payload.phone ?? user?.phone,
          preferred_language: data?.preferred_language || payload.preferred_language || 'en',
        });
      }
      setNote(`Saved. Chat replies will use ${languageLabel}.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setNote(
        typeof detail === 'string'
          ? detail
          : 'Could not save just now. Try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="by-page">
      <div className="by-center">
        <header className="by-page-head">
          <h1>Settings</h1>
          <p className="by-lede">
            Set the language for written chat replies. Name, phone, and email stay fixed after the first time they are saved.
          </p>
        </header>

        <div className="by-setting">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6 }}>
            <UserAvatar user={user} size="lg" style={{ width: 56, height: 56, fontSize: 22 }} />
            <div>
              <h2 style={{ margin: 0 }}>{name || 'Your profile'}</h2>
              <p className="by-lede" style={{ margin: '4px 0 0' }}>
                {isPro ? 'ByOnco PRO' : 'Free account'} · {user?.email || 'No email on file'}
              </p>
            </div>
          </div>
          <form onSubmit={save}>
            <label className="by-field">
              <span>Full name{lockedName ? ' (locked)' : ''}</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="First and last name"
                autoComplete="name"
                maxLength={80}
                readOnly={lockedName}
                aria-readonly={lockedName ? 'true' : undefined}
              />
            </label>
            <label className="by-field">
              <span>Phone{lockedPhone ? ' (locked)' : ' (optional until set)'}</span>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 …"
                autoComplete="tel"
                maxLength={32}
                readOnly={lockedPhone}
                aria-readonly={lockedPhone ? 'true' : undefined}
              />
            </label>
            <label className="by-field">
              <span>Email (locked)</span>
              <input value={user?.email || ''} readOnly aria-readonly="true" />
            </label>

            <div className="by-field">
              <span>Chat reply language</span>
              <p className="by-fine" style={{ margin: '0 0 10px', textAlign: 'left' }}>
                English is the default. Pick any of the 18 writing languages. Vani will answer typed chat in that language.
              </p>
              <div className="by-row by-lang-grid" role="group" aria-label="Chat reply language">
                {WRITING_LANGS.map((row) => (
                  <button
                    key={row.code}
                    type="button"
                    className={`by-chip${language === row.code ? ' is-on' : ''}`}
                    onClick={() => setLanguage(row.code)}
                    aria-pressed={language === row.code}
                  >
                    {row.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="by-row">
              <button type="submit" className="by-btn by-btn-dark" disabled={busy}>
                {busy ? 'Saving…' : 'Save profile'}
              </button>
            </div>
            {note ? (
              <p className={`by-save-note${/could not|please add|cannot be changed/i.test(note) ? ' is-err' : ' is-ok'}`}>{note}</p>
            ) : null}
          </form>
        </div>

        <Link
          className="by-link-row"
          to={memberPath('/voice')}
          onClick={(e) => {
            if (!isPro) {
              e.preventDefault();
              setUpgradeOpen(true);
            }
          }}
        >
          <span className="by-link-ico" aria-hidden="true"><IconMic /></span>
          <span className="by-link-row-copy">
            <b>Voice {isPro ? null : <span className="by-pro">PRO</span>}</b>
            <small>Talk instead of type. Same help as text: side effects, food, medicines, labs, clinic prep.</small>
          </span>
          <span className="by-chev" aria-hidden="true"><IconArrow /></span>
        </Link>

        <Link className="by-link-row" to={memberPath('/account')}>
          <span className="by-link-ico" aria-hidden="true"><IconCrown /></span>
          <span className="by-link-row-copy">
            <b>Plan and billing</b>
            <small>{isPro ? 'Manage PRO, lifetime, or cancel.' : 'Monthly $19.99, yearly $14.99/mo, or $999 once.'}</small>
          </span>
          <span className="by-chev" aria-hidden="true"><IconArrow /></span>
        </Link>

        <DataControls />
      </div>
    </div>
  );
}

function DataControls() {
  const { logout } = useAuth();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [askDelete, setAskDelete] = useState(false);
  const [typed, setTyped] = useState('');
  const canDelete = typed.trim().toLowerCase() === 'delete';

  useEffect(() => {
    if (!askDelete) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key === 'Escape' && !busy) {
        setAskDelete(false);
        setTyped('');
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [askDelete, busy]);

  const exportPdf = async () => {
    setBusy('pdf');
    setNote('Preparing your record…');
    try {
      const response = await fetch(apiUrl('/api/me/export?format=pdf'), {
        credentials: 'include',
        headers: authHeaders(),
      });
      if (response.status === 429) {
        setNote('Please wait a little, then try the download again.');
        return;
      }
      if (!response.ok) {
        setNote('Could not prepare the PDF. Try again in a moment.');
        return;
      }
      const kind = response.headers.get('Content-Type') || '';
      if (!kind.includes('pdf')) {
        setNote('Could not prepare the PDF. Try again in a moment.');
        return;
      }
      const blob = await response.blob();
      const match = /filename="([^"]+)"/.exec(response.headers.get('Content-Disposition') || '');
      downloadBlob(blob, match?.[1] || 'ByOnco-record.pdf');
      setNote('Your record is downloaded. Keep the PDF somewhere private.');
    } catch {
      setNote('Could not prepare the PDF. Try again in a moment.');
    } finally {
      setBusy('');
    }
  };

  const exportJson = async () => {
    setBusy('json');
    setNote('Preparing a machine copy…');
    try {
      const response = await fetch(apiUrl('/api/me/export'), {
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!response.ok) {
        setNote('Could not prepare the machine copy. Try again in a moment.');
        return;
      }
      const blob = new Blob([JSON.stringify(await response.json(), null, 2)], { type: 'application/json' });
      downloadBlob(blob, 'ByOnco-record.json');
      setNote('Machine copy downloaded. The PDF is the one meant for reading.');
    } catch {
      setNote('Could not prepare the machine copy. Try again in a moment.');
    } finally {
      setBusy('');
    }
  };

  const deleteAccount = async () => {
    if (!canDelete || busy) return;
    setBusy('delete');
    setNote('');
    try {
      const response = await fetch(apiUrl('/api/me/delete'), {
        method: 'POST',
        credentials: 'include',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = data.detail;
        const text = typeof detail === 'string' ? detail : detail?.message || data.message;
        setAskDelete(false);
        setTyped('');
        setNote(
          response.status === 423
            ? 'This account cannot be deleted right now because of a legal hold. Email care@getbyonco.com.'
            : (text || 'Deletion failed. Try again, or email care@getbyonco.com.')
        );
        return;
      }
      setNote('Your records have been deleted.');
      await logout();
    } catch {
      setNote('Deletion failed. Try again, or email care@getbyonco.com.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="by-setting">
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <span className="by-link-ico" aria-hidden="true"><IconDoc /></span>
        <div style={{ flex: 1 }}>
          <h2>Your records</h2>
          <p className="by-lede" style={{ margin: '0 0 4px', maxWidth: 'none' }}>
            Download a readable copy of your conversations, check-ins, and reports, or delete them from this account.
            A legal hold can block deletion. This is your ByOnco record, ready to download or remove.
          </p>
          <div className="by-row">
            <button type="button" className="by-btn by-btn-dark" disabled={!!busy} onClick={exportPdf}>
              {busy === 'pdf' ? 'Preparing PDF…' : 'Download my record'}
            </button>
            <button type="button" className="by-btn by-btn-ghost" disabled={!!busy} onClick={exportJson}>
              {busy === 'json' ? 'Preparing…' : 'Machine copy'}
            </button>
            <button
              type="button"
              className="by-btn by-btn-danger"
              disabled={!!busy}
              onClick={() => { setAskDelete(true); setTyped(''); }}
            >
              Delete my data
            </button>
          </div>
          <p className="by-fine" style={{ textAlign: 'left', marginTop: 10 }}>
            The PDF is the one to keep and take to clinic. Machine copy is JSON, for your own records or another system.
          </p>
          {note ? (
            <p className={`by-save-note${/could not|fail|hold/i.test(note) ? ' is-err' : ' is-ok'}`}>{note}</p>
          ) : null}
        </div>
      </div>

      {askDelete ? (
        <div
          className="by-modal-back"
          role="dialog"
          aria-modal="true"
          aria-labelledby="by-delete-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !busy) {
              setAskDelete(false);
              setTyped('');
            }
          }}
        >
          <div className="by-modal by-modal-narrow">
            <button
              type="button"
              className="by-icon-btn"
              style={{ position: 'absolute', top: 16, right: 16 }}
              onClick={() => { if (!busy) { setAskDelete(false); setTyped(''); } }}
              aria-label="Close"
            >
              <IconClose />
            </button>
            <p className="by-bill-kicker">This cannot be undone</p>
            <h2 id="by-delete-title">Delete your ByOnco records?</h2>
            <p className="by-lede" style={{ maxWidth: 'none' }}>
              This removes conversations with Vani, check-ins, saved answers, memories, and reports you uploaded.
              Billing records may be kept where the law requires it. You will be signed out when it is done.
            </p>
            <label className="by-field">
              <span>Type Delete to confirm</span>
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoFocus
                autoComplete="off"
                placeholder="Delete"
                disabled={!!busy}
              />
            </label>
            <div className="by-row">
              <button
                type="button"
                className="by-btn by-btn-ghost"
                disabled={!!busy}
                onClick={() => { setAskDelete(false); setTyped(''); }}
              >
                Keep my records
              </button>
              <button
                type="button"
                className="by-btn by-btn-danger-fill"
                disabled={!canDelete || !!busy}
                onClick={deleteAccount}
              >
                {busy === 'delete' ? 'Deleting…' : 'Delete records'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
